import { useCallback } from 'react'
import { ApiChannel, GiftCard } from '@vend/types'
import { ToastNotifications } from '@lightspeed/design-system-react'
import { useT } from '@transifex/react'
import { channelsStore } from '../../factories/database/channels-store'
import { giftCardsResource } from '../../ng-compat/managers'
import { ERRORS } from './errors'
import { useRedeemGiftCard } from './useRedeemGiftCard'

const errNoBigCommerceChannel = new Error('No BigCommerce channel found')
export const useBigCommerceGiftCerts = () => {
  const t = useT()
  const { getPayableAmount } = useRedeemGiftCard()
  /**
   * Retrieve the details of the current gift card.
   */
  const retrieveBigCommerceGiftCard = useCallback(
    async (giftcardCode: string) => {
      try {
        const channels: ApiChannel[] = await channelsStore.getByChannelType(
          'bigcommerce'
        )
        if (channels.length < 1) {
          throw errNoBigCommerceChannel
        }
        const channelId = channels[0].id
        const giftCard = await giftCardsResource.getGiftCardByCode(
          channelId,
          giftcardCode
        )
        if (!giftCard) {
          ToastNotifications.negative(
            t(
              'Gift certificate not activated. Please check again or enter another number.',
              {
                _context: 'Big commerce gift certs not activated',
              }
            )
          )
        }
        return giftCard
      } catch (response: any) {
        const tContext = {
          _context: 'Error message when retrieve big commerce gift certs',
        }
        if (response === errNoBigCommerceChannel) {
          ToastNotifications.negative(
            t('Sorry, could not find a BigCommerce channel.', tContext)
          )
        } else if (response?.status === 0) {
          ToastNotifications.negative(
            t(
              'Gift certificates can not be redeemed when the Sell screen is offline.',
              tContext
            )
          )
        } else {
          ToastNotifications.negative(
            t('Sorry, something went wrong. Please try again.', tContext)
          )
        }
      }
    },
    [t]
  )

  /**
   * Makes a payment against the provided gift card.
   */
  const payWithGiftCard = useCallback(
    async (giftcard: GiftCard, amountTendered: number) => {
      const payableAmount = getPayableAmount(giftcard, amountTendered)
      try {
        const channels: ApiChannel[] = await channelsStore.getByChannelType(
          'bigcommerce'
        )
        if (channels.length < 1) {
          throw errNoBigCommerceChannel
        }
        const channelId = channels[0].id
        const giftCardId = giftcard.id
        const transactionId = await giftCardsResource.redeemGiftCard(
          channelId,
          giftCardId,
          payableAmount
        )

        return {
          amountTendered: payableAmount,
          source: 'BigCommerce Gift Certificate',
          source_id: transactionId,
        }
      } catch (error: any) {
        const tContext = {
          _context: 'Error message when pay with big commerce gift certs',
        }
        if (error?.message) {
          switch (error.message) {
            case ERRORS.EXPIRED:
              ToastNotifications.negative(
                t('Sorry, Gift Certificate has expired.', tContext)
              )
              break
            case ERRORS.INSUFFICIENT_FUNDS:
              ToastNotifications.negative(
                t('Sorry, insufficient funds available.', tContext)
              )
              break
            case ERRORS.NOT_FOUND:
              ToastNotifications.negative(
                t('Sorry, Gift Certificate couldn’t be found.', tContext)
              )
              break
            default:
              ToastNotifications.negative(
                t('Sorry, something went wrong. Please try again.', tContext)
              )
              break
          }
        } else {
          ToastNotifications.negative(
            t('Sorry, something went wrong. Please try again.', tContext)
          )
        }
      }
    },
    [getPayableAmount, t]
  )
  return {
    retrieveBigCommerceGiftCard,
    payWithGiftCard,
  }
}
